import { useEffect, useMemo, useState } from 'react';
import { useAudienceStore } from '../stores/audience';
import {
  getAudienceById,
  getAudienceByPath,
  type AudienceMeta,
} from '@shared/constants/audiences';

/**
 * Hook unifié pour la résolution du profil audience actif.
 *
 * Priorité de résolution :
 *   1. Profil verrouillé (lockedAudience — URL /[audience]/[outil], activé en PR2+)
 *   2. URL pathname         — page d'audience directe (/particuliers, /artisans…)
 *   3. Store Zustand        — profil mémorisé en localStorage + miroir cookie
 *
 * Retourne un objet stable avec :
 *   - `fromUrl`   : audience détectée depuis le pathname seul (null hors page audience)
 *   - `fromStore` : audience mémorisée dans le store (null si aucune)
 *   - `fromLock`  : audience verrouillée par /[audience]/[outil] (toujours null en PR1)
 *   - `effective` : audience retenue après application de la priorité
 *   - `source`    : origine de `effective` ('url' | 'store' | 'lock' | null)
 *
 * Côté SSR : toutes les valeurs sont null jusqu'à hydratation cliente
 * (évite les hydration mismatches).
 *
 * Réagit automatiquement à :
 * - changements d'`audienceId` dans le store (Dropdown / Modal)
 * - changements d'URL (navigation Vike vike:navigate / popstate)
 */
export function useEffectiveAudience(): {
  fromUrl: AudienceMeta | null;
  fromStore: AudienceMeta | null;
  fromLock: AudienceMeta | null;
  effective: AudienceMeta | null;
  source: 'url' | 'store' | 'lock' | null;
} {
  const audienceIdFromStore = useAudienceStore((s) => s.audienceId);
  const lockedAudienceId = useAudienceStore((s) => s.lockedAudienceId);
  const hydrated = useAudienceStore((s) => s.hydrated);
  const hydrate = useAudienceStore((s) => s.hydrate);
  const [pathname, setPathname] = useState<string | null>(null);

  useEffect(() => {
    hydrate();
    setPathname(window.location.pathname);
    const onNavigate = () => setPathname(window.location.pathname);
    window.addEventListener('vike:navigate', onNavigate);
    window.addEventListener('popstate', onNavigate);
    return () => {
      window.removeEventListener('vike:navigate', onNavigate);
      window.removeEventListener('popstate', onNavigate);
    };
  }, [hydrate]);

  return useMemo(() => {
    const fromUrl = pathname ? (getAudienceByPath(pathname) ?? null) : null;
    const fromStore =
      hydrated && audienceIdFromStore ? (getAudienceById(audienceIdFromStore) ?? null) : null;
    // Priorité maximale : profil verrouillé par une URL /[audience]/[outil]
    const fromLock =
      hydrated && lockedAudienceId ? (getAudienceById(lockedAudienceId) ?? null) : null;

    if (fromLock) {
      return { fromUrl, fromStore, fromLock, effective: fromLock, source: 'lock' as const };
    }
    if (fromUrl) {
      return { fromUrl, fromStore, fromLock, effective: fromUrl, source: 'url' as const };
    }
    if (fromStore) {
      return { fromUrl, fromStore, fromLock, effective: fromStore, source: 'store' as const };
    }
    return { fromUrl, fromStore, fromLock, effective: null, source: null };
  }, [pathname, audienceIdFromStore, lockedAudienceId, hydrated]);
}
